import { cache } from "react";
import { ApiResponseError } from "@/lib/error";
import { operations } from "@/openapi/generated";
import type { ContractStatus, MerchantStatus } from "@/openapi/generated";
import { api } from "@/openapi/client";
import { getCacheTags } from "@/lib/cache-utils";
import { shopQueryKeys } from "@/app/admin/shops/_api/shop-query-keys";

export type ShopsByCategoryIdParameter = operations["getByCategory"]["parameters"]["query"];
export type ShopsByCategoryIdResponse = operations["getByCategory"]["responses"]["200"]["content"]["*/*"];

export async function getShopsByCategoryId(
    query: ShopsByCategoryIdParameter,
    client: typeof api = api
): Promise<ShopsByCategoryIdResponse> {
  const { data, error } = await client.GET("/shops/byCategory",
      {
        params: { query },
        next: { tags: getCacheTags(shopQueryKeys.byCategory(query)) },
        cache: 'force-cache'
      },
  );
  if (error) throw error;
  if (!data) throw new ApiResponseError("getShopsByCategoryId");
  return data;
}


export type SearchShopsByNameParameter = operations["searchShops"]["parameters"]["query"];
export type SearchShopsResponse = operations["searchShops"]["responses"]["200"]["content"]["*/*"];

export async function searchShops(
    query: SearchShopsByNameParameter,
    client: typeof api = api
): Promise<SearchShopsResponse> {
  const { data, error } = await client.GET("/shops/search",
      {
        params: { query },
        next: { tags: getCacheTags(shopQueryKeys.search({
          term: query.term || '',
          categoryId: query.categoryId,
          page: query.page,
          size: query.size || 30
        })) },
      },
  );
  if (error) throw error;
  if (!data) throw new ApiResponseError("search");
  return data;
}

export type GetAllShopsParameter = operations["getAllShops"]["parameters"]["query"];
export type GetAllShopsResponse = operations["getAllShops"]["responses"]["200"]["content"]["*/*"];

export async function getAllShops(
    query: GetAllShopsParameter,
    client: typeof api = api,
    fetchOptions?: { cache?: RequestCache }
): Promise<GetAllShopsResponse> {
  const cache = fetchOptions?.cache ?? 'force-cache';
  const { data, error } = await client.GET("/shops",
      {
        params: { query },
        next: cache === 'no-store' ? undefined : { tags: getCacheTags(shopQueryKeys.allShops({
          page: query?.page,
          size: query?.size || 30
        })) },
        cache,
      },
  );
  if (error) throw error;
  if (!data) throw new ApiResponseError("getAllShops");
  return data;
}


export type ShopsWithAssociationsParameter = operations["getShopsWithAssociations"]["parameters"]["query"];
export type ShopsWithAssociationsResponse = operations["getShopsWithAssociations"]["responses"]["200"]["content"]["*/*"];

// Extends the generated type to support contractStatuses, merchantStatuses (multi-select), and uncategorized filter.
// The backend @RequestParam contractStatus/merchantStatuses accepts repeated values,
// which openapi-fetch serializes as ?contractStatus=A&contractStatus=B (same as status[]).
type ShopsWithAssociationsExtendedParameter = ShopsWithAssociationsParameter & {
  contractStatuses?: ContractStatus[];
  merchantStatuses?: MerchantStatus[];
  monetized?: boolean;
  uncategorized?: boolean;
};

/* WARNING: CUSTOM SORTING NOT APPLIED ON THIS ENDPOINT */
export async function getShopsWithAssociations(
    query: ShopsWithAssociationsExtendedParameter,
    client: typeof api = api
): Promise<ShopsWithAssociationsResponse> {
  const { contractStatuses, merchantStatuses, monetized, uncategorized, ...rest } = query;
  const apiQuery = {
    ...rest,
    ...(contractStatuses?.length ? { contractStatus: contractStatuses as unknown as ContractStatus } : {}),
    ...(merchantStatuses?.length ? { merchantStatuses } : {}),
    ...(monetized !== undefined ? { monetized } : {}),
    ...(uncategorized !== undefined ? { uncategorized } : {}),
  };
  const { data, error } = await client.GET("/shops/withAssociations", {
        params: { query: apiQuery },
        next: { tags: getCacheTags(shopQueryKeys.withAssociations({
          page: query?.page,
          size: query?.size,
          name: query?.name,
          sort: query?.sort,
          status: query?.status,
          createdAtFrom: query?.createdAtFrom,
          createdAtTo: query?.createdAtTo,
          updatedAtFrom: query?.updatedAtFrom,
          updatedAtTo: query?.updatedAtTo,
          contractStatuses: query?.contractStatuses,
          merchantStatuses: query?.merchantStatuses,
          monetized: query?.monetized,
          uncategorized: query?.uncategorized,
        })) },
        cache: 'force-cache'
      },
  );
  if (error) throw error;
  if (!data) throw new ApiResponseError("getShopsWithAssociations");
  return data;
}

export type ShopByIdRequestParameter = operations["getItemResource-shop-get"]["parameters"]["path"]["id"];
export type ShopByIdResponse = operations["getItemResource-shop-get"]["responses"]["200"]["content"]["*/*"];

export async function getShopById(
    id: ShopByIdRequestParameter,
    client: typeof api = api
): Promise<ShopByIdResponse> {
  const { data, error } = await client.GET("/shops/{id}", {
    params: { path: { id } },
    next: { tags: getCacheTags(shopQueryKeys.detail(id)) },
    cache: 'force-cache'
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getShopById");
  return data;
}

export type ShopBySlugRequestParameter = operations["getShopBySlug"]["parameters"]["path"]["slug"];
export type ShopBySlugResponse = operations["getShopBySlug"]["responses"]["200"]["content"]["*/*"];

export async function getShopBySlug(
    slug: ShopBySlugRequestParameter,
    client: typeof api = api
): Promise<ShopBySlugResponse> {
  // PG slug comparison is case-sensitive — normalize before passing in.
  const normalizedSlug = slug.toLowerCase();
  const { data, error } = await client.GET("/shops/bySlug/{slug}", {
    params: { path: { slug: normalizedSlug } },
    next: { tags: getCacheTags(shopQueryKeys.detailBySlug(normalizedSlug)) },
    cache: 'force-cache'
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getShopBySlug");
  return data;
}

// Request-scoped deduplication: generateMetadata and the page function
// share one network call when both invoke this within the same request.
export const getCachedShopBySlug = cache(getShopBySlug);

export type ShopByIdWithAssociationsParameter = operations["getShopsWithAssociationsById"]["parameters"]["path"]["id"];
export type ShopByIdWithAssociationsResponse = operations["getShopsWithAssociationsById"]["responses"]["200"]["content"]["*/*"];

export async function getShopByIdWithAssociations(
    id: ShopByIdWithAssociationsParameter,
    client: typeof api = api
): Promise<ShopByIdWithAssociationsResponse> {
  const { data, error } = await client.GET("/shops/withAssociations/{id}", {
    params: { path: { id } },
    next: { tags: getCacheTags(shopQueryKeys.detailWithAssociations(id)) },
    cache: 'force-cache'
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getShopByIdWithAssociations");
  return data;
}

export type PopularShopsRequest = operations["getPopularShops"]["parameters"]["query"]
export type PopularShopsResponse = operations["getPopularShops"]["responses"]["200"]["content"]["*/*"]

export async function getPopularShops(
    query: PopularShopsRequest,
    client: typeof api = api
): Promise<PopularShopsResponse> {
  const { data, error } = await client.GET("/shops/popular", {
    params: { query },
    next: { tags: getCacheTags(shopQueryKeys.popular(query)) },
    cache: 'force-cache'
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getPopularShops");
  return data;
}

// Extension config types (will also appear in generated.ts after API regeneration)
export type ExtensionPlatform = "IOS" | "ANDROID" | "WEB";
export type ShopExtensionConfigDto = {
  reminderEnabled?: boolean;
  discountCodesEnabled?: boolean;
  recentTransactionsEnabled?: boolean;
  serpAnnotationEnabled?: boolean;
};
export type ShopExtensionConfigResponse = Partial<Record<ExtensionPlatform, ShopExtensionConfigDto>>;


